// apps/admin/src/features/billing/NoteFormBase.tsx
//
// Phase 8.2 Wave 3 Task 14 — Shared form skeleton for Credit Note /
// Debit Note issuance modals.
//
// Per CONTEXT.md
//   • Invoice numbering format (CN-YYYYMM-XXXXX
//                  DN-YYYYMM-XXXXX, generated server-side and surfaced
//                  in the success toast).
//   • Credit/Debit Note flow: admin chủ động tạo từ invoice
//                  detail (modal: amount + reason + line items selector);
//                  backend generates PDF + audit log.
//   • CN/DN luôn có audit_log_id + actor + reason; reason
//                  required ≥10 chars.
//
// Per API-CONTRACTS.md
//   • POST /api/v1/admin/invoices/:id/credit-notes
//     body: { amount_cents (>=1), reason (10..1000),
//             line_item_ids?: string[] }
//   • POST /api/v1/admin/invoices/:id/debit-notes
//     body: { amount_cents (>=1), reason (10..1000),
//             line_item_description (5..500) }
//
// This module ONLY owns the shared scaffold (amount input + reason
// textarea + validation error display + USD canonical hint). Each
// concrete modal (IssueCreditNoteModal / IssueDebitNoteModal) composes
// its own variant-specific extras (multi-select for CN, free-text
// description for DN) and dispatches to its endpoint.
//
// vg-binding: PLAN:task-14
// vg-binding: P8.D-455
// vg-binding: P8.D-456
// vg-binding: BR-11
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:idempotency-key

import type { ChangeEvent, ReactNode } from 'react';

import { cn } from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Shared constants — mirror BE zod schemas verbatim.
// ---------------------------------------------------------------------------

export const REASON_MIN = 10;
export const REASON_MAX = 1000;
export const AMOUNT_CENTS_MIN = 1;

// ---------------------------------------------------------------------------
// Idempotency helper — both modals call sibling mutating endpoints and
// must forward an Idempotency-Key per INTERFACE-STANDARDS.
// ---------------------------------------------------------------------------

export function newNoteIdempotencyKey(_kind: 'credit-note' | 'debit-note'): string {
  // BE idempotency middleware validates strict UUID v4; prefix variant
  // (`credit-note-<uuid>`) was rejected with 400 "Idempotency-Key must
  // be a UUID v4" (debug-bill iter 10c). The `_kind` argument is kept
  // for caller-site clarity but not embedded in the wire value.
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  // RFC 4122 v4 fallback for environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ---------------------------------------------------------------------------
// USD-cents formatter — matches `<Money>` shape so the in-modal hint and
// the eventual toast use the same canonical format.
// ---------------------------------------------------------------------------

export function formatUsdCentsCompact(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return '$0.00 USD';
  const dollars = cents / 100;
  return `${new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars)} USD`;
}

/**
 * Parse a human dollar input ("12.34" / "12") into integer USD cents.
 * Returns null when the input is empty / non-numeric / negative so the
 * caller can surface a localized validation error.
 */
export function parseUsdDollarsToCents(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  // Multiply then round to avoid 12.34 → 1233.9999...
  return Math.round(parsed * 100);
}

// ---------------------------------------------------------------------------
// Shared base props
// ---------------------------------------------------------------------------

export interface NoteFormBaseProps {
  /** USD dollars input (raw string — caller parses with `parseUsdDollarsToCents`). */
  amountDollars: string;
  onAmountDollarsChange: (next: string) => void;
  /** Reason text (>=10 chars). */
  reason: string;
  onReasonChange: (next: string) => void;
  /** Per-field error keys (null when valid). */
  amountError?: string | null;
  reasonError?: string | null;
  /** Disable inputs while mutation is in flight. */
  disabled?: boolean;
  /** Slot for variant-specific extras (CN line-item multi-select or
   *  DN line-item description textarea). Rendered below reason. */
  extras?: ReactNode;
  /** Optional pre-filled hint of the invoice total (USD cents). */
  invoiceTotalCentsHint?: number | null;
  /** Stable test-id prefix — both modals share input test-ids. */
  amountInputTestId?: string;
  reasonInputTestId?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * `<NoteFormBase>` — shared scaffold for CN/DN issuance forms. Owns
 * amount + reason inputs and renders per-field errors. Variant-specific
 * fields (CN line-item multi-select / DN line-item description) plug in
 * via the `extras` slot so both modals stay DRY.
 */
export function NoteFormBase(props: NoteFormBaseProps): JSX.Element {
  const {
    amountDollars,
    onAmountDollarsChange,
    reason,
    onReasonChange,
    amountError = null,
    reasonError = null,
    disabled = false,
    extras = null,
    invoiceTotalCentsHint = null,
    amountInputTestId = 'cn-dn-amount-input',
    reasonInputTestId = 'cn-dn-reason-input',
  } = props;

  const { t } = useTranslation('finance');
  const reasonLen = reason.trim().length;
  const reasonRemainingHint =
    reasonLen < REASON_MIN
      ? t('invoice.noteForm.reasonRemaining', {
          count: Math.max(0, REASON_MIN - reasonLen),
          min: REASON_MIN,
        })
      : t('invoice.noteForm.reasonCount', { count: reasonLen, max: REASON_MAX });

  return (
    <div className="space-y-4">
      {/* Amount input — USD canonical. */}
      <div className="space-y-1.5">
        <label
          htmlFor="cn-dn-amount-input"
          className="text-sm font-medium leading-none text-pw-text"
        >
          {t('invoice.noteForm.amountLabel')} <span className="text-red-600 dark:text-red-300">*</span>
        </label>
        <input
          id="cn-dn-amount-input"
          data-testid={amountInputTestId}
          inputMode="decimal"
          type="text"
          autoComplete="off"
          className={cn(
            'flex h-10 w-full rounded-md border bg-field px-3 py-2 text-sm ring-offset-background',
            'placeholder:text-pw-text-muted focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-pw-primary focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            amountError ? 'border-red-500' : 'border-pw-border',
          )}
          placeholder={t('invoice.noteForm.amountPlaceholder')}
          value={amountDollars}
          disabled={disabled}
          onChange={(ev: ChangeEvent<HTMLInputElement>) =>
            onAmountDollarsChange(ev.target.value)
          }
          aria-invalid={amountError ? true : undefined}
          aria-describedby="cn-dn-amount-hint"
        />
        <div
          id="cn-dn-amount-hint"
          className="flex items-center justify-between text-xs"
        >
          <span className="text-pw-text-muted">
            {t('invoice.noteForm.amountHint')}
          </span>
          {invoiceTotalCentsHint != null && invoiceTotalCentsHint > 0 ? (
            <span className="text-pw-text-muted">
              {t('invoice.noteForm.invoiceTotal', {
                amount: formatUsdCentsCompact(invoiceTotalCentsHint),
              })}
            </span>
          ) : null}
        </div>
        {amountError ? (
          <p
            className="text-xs font-medium text-red-600 dark:text-red-300"
            data-testid="cn-dn-amount-error"
          >
            {amountError}
          </p>
        ) : null}
      </div>

      {/* Reason textarea — required, min 10 chars. */}
      <div className="space-y-1.5">
        <label
          htmlFor="cn-dn-reason-input"
          className="text-sm font-medium leading-none text-pw-text"
        >
          {t('invoice.noteForm.reasonLabel')} <span className="text-red-600 dark:text-red-300">*</span>
        </label>
        <textarea
          id="cn-dn-reason-input"
          data-testid={reasonInputTestId}
          rows={3}
          maxLength={REASON_MAX}
          className={cn(
            'flex min-h-[80px] w-full rounded-md border bg-field px-3 py-2 text-sm ring-offset-background',
            'placeholder:text-pw-text-muted focus-visible:outline-none',
            'focus-visible:ring-2 focus-visible:ring-pw-primary focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            reasonError ? 'border-red-500' : 'border-pw-border',
          )}
          placeholder={t('invoice.noteForm.reasonPlaceholder')}
          value={reason}
          disabled={disabled}
          onChange={(ev: ChangeEvent<HTMLTextAreaElement>) =>
            onReasonChange(ev.target.value)
          }
          aria-invalid={reasonError ? true : undefined}
          aria-describedby="cn-dn-reason-hint"
        />
        <div
          id="cn-dn-reason-hint"
          className={cn(
            'text-xs',
            reasonError ? 'text-red-600 dark:text-red-300' : 'text-pw-text-muted',
          )}
        >
          {reasonError ?? reasonRemainingHint}
        </div>
      </div>

      {/* Variant-specific extras (CN multi-select / DN description). */}
      {extras}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared invoice line-item shape (consumed by both modals — CN renders
// the multi-select, DN may surface the same list as read-only context).
// ---------------------------------------------------------------------------

export interface InvoiceLineItemSummary {
  id: string;
  description: string;
  amount_cents: number;
  quantity?: number;
}

// ---------------------------------------------------------------------------
// Shared response envelopes — both endpoints return matching shapes
// with the only divergence being the number field name. Modals destruc-
// ture either `credit_note_number` or `debit_note_number` accordingly.
// ---------------------------------------------------------------------------

export interface IssueCreditNoteResponse {
  id: string;
  credit_note_number: string;
  invoice_id: string;
  amount_cents: number;
  pdf_url: string;
  issued_at: string;
  audit_log_id: string;
}

export interface IssueDebitNoteResponse {
  id: string;
  debit_note_number: string;
  invoice_id: string;
  amount_cents: number;
  pdf_url: string;
  issued_at: string;
  audit_log_id: string;
}
